// src/pages/EventDashboard.js
// Per-event dashboard with Overview and Attendees tabs.
// Route: /dashboard/events/:eventId

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getEventById, subscribeToRegistrations, removeEventAttendee, exportEventAttendees,
  sendEventGroupMessage, shareEventRecording,
} from '../services/event-service';
import {
  Users, ArrowLeft, Download, Mail,
  MoreHorizontal, Trash2, Loader2, CheckCircle,
  Send, Video,
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function formatDateShort(d) {
  if (!d) return '';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatEventSummary(event) {
  if (!event) return '';
  if (event.eventType === 'single' && event.schedule?.date) {
    return `${formatDateShort(event.schedule.date)} • ${formatTime12(event.schedule.startTime)} — ${formatTime12(event.schedule.endTime)}`;
  }
  if (event.eventType === 'series' && event.schedule?.sessionDates?.length > 0) {
    return `${event.schedule.sessionDates.length} sessions starting ${formatDateShort(event.schedule.sessionDates[0])}`;
  }
  if (event.eventType === 'multi_day' && event.schedule?.date) {
    return `${formatDateShort(event.schedule.date)} — ${formatDateShort(event.schedule.endDate)}`;
  }
  return '';
}

function getFormatLabel(f) {
  return f === 'virtual' ? 'Virtual' : f === 'in_person' ? 'In-Person' : f === 'hybrid' ? 'Hybrid' : '';
}

function getStatusBadge(s) {
  if (s === 'published') return { label: 'Published', bg: '#F0FDF4', color: '#16A34A' };
  if (s === 'draft') return { label: 'Draft', bg: '#F3F4F6', color: '#6B7280' };
  if (s === 'cancelled') return { label: 'Cancelled', bg: '#FEF2F2', color: '#DC2626' };
  if (s === 'completed') return { label: 'Completed', bg: '#EFF6FF', color: '#2563EB' };
  return { label: s, bg: '#F3F4F6', color: '#6B7280' };
}

function getPaymentBadge(ps) {
  if (ps === 'paid') return { label: 'Paid', bg: '#F0FDF4', color: '#16A34A' };
  if (ps === 'free') return { label: 'Free', bg: '#F0FDF4', color: '#16A34A' };
  if (ps === 'deposit_paid') return { label: 'Deposit', bg: '#FFFBEB', color: '#D97706' };
  if (ps === 'balance_due') return { label: 'Balance due', bg: '#FFFBEB', color: '#D97706' };
  if (ps === 'balance_overdue') return { label: 'Overdue', bg: '#FEF2F2', color: '#DC2626' };
  if (ps === 'refunded') return { label: 'Refunded', bg: '#F3F4F6', color: '#6B7280' };
  if (ps === 'pending') return { label: 'Pending', bg: '#FFFBEB', color: '#D97706' };
  return { label: ps || '—', bg: '#F3F4F6', color: '#6B7280' };
}

// ─── Component ──────────────────────────────────────────────────────────────

const EventDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [uid, setUid] = useState(null);
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);

  // Communications state
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState('');

  // Recording state
  const [recUrl, setRecUrl] = useState('');
  const [recExpiry, setRecExpiry] = useState('thirty_days');
  const [sharingRec, setSharingRec] = useState(false);
  const [recShared, setRecShared] = useState(false);

  // Auth
  useEffect(() => {
    let done = false;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && !done) { done = true; setUid(user.uid); }
    });
    const timeout = setTimeout(() => { if (!done) setLoading(false); }, 3000);
    return () => { unsub(); clearTimeout(timeout); };
  }, []);

  // Load event + registrations
  useEffect(() => {
    if (!uid || !eventId) return;
    let unsubRegs = () => {};

    (async () => {
      const evt = await getEventById(uid, eventId);
      setEvent(evt);
      setLoading(false);

      if (evt) {
        unsubRegs = subscribeToRegistrations(uid, eventId, (regs) => {
          setRegistrations(regs);
        });
      }
    })();

    return () => unsubRegs();
  }, [uid, eventId]);

  // ── Handlers ──
  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      const result = await exportEventAttendees(eventId);
      if (result.csv) {
        const blob = new Blob([result.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || 'attendees.csv';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  }, [eventId]);

  const handleRemoveAttendee = useCallback(async (reg) => {
    if (!window.confirm(`Remove ${reg.attendeeName || reg.attendeeEmail}? ${reg.totalPaid > 0 ? 'A full refund will be processed.' : ''}`)) return;
    try {
      await removeEventAttendee(eventId, reg.id);
    } catch (err) {
      console.error('Remove attendee error:', err);
      alert('Failed: ' + (err.message || 'Unknown error'));
    }
    setMenuOpenId(null);
  }, [eventId]);

  const handleSendGroupMessage = useCallback(async () => {
    if (!msgBody.trim()) return;
    setSendingMsg(true);
    setMsgSuccess('');
    try {
      const result = await sendEventGroupMessage(eventId, msgSubject, msgBody);
      setMsgSuccess(`Message sent to ${result.sentCount} attendee${result.sentCount !== 1 ? 's' : ''}`);
      setMsgSubject('');
      setMsgBody('');
      // Refresh event to get updated communications
      const updated = await getEventById(uid, eventId);
      if (updated) setEvent(updated);
    } catch (err) {
      console.error('Group message error:', err);
      alert('Failed to send: ' + (err.message || 'Unknown error'));
    } finally {
      setSendingMsg(false);
    }
  }, [eventId, msgSubject, msgBody, uid]);

  const handleShareRecording = useCallback(async () => {
    if (!recUrl.trim()) return;
    setSharingRec(true);
    try {
      await shareEventRecording(eventId, recUrl, recExpiry);
      setRecShared(true);
      const updated = await getEventById(uid, eventId);
      if (updated) setEvent(updated);
    } catch (err) {
      console.error('Share recording error:', err);
      alert('Failed to share: ' + (err.message || 'Unknown error'));
    } finally {
      setSharingRec(false);
    }
  }, [eventId, recUrl, recExpiry, uid]);

  // Initialize recording state from event data
  useEffect(() => {
    if (event?.recordingUrl) {
      setRecUrl(event.recordingUrl);
      setRecExpiry(event.recordingExpiry || 'thirty_days');
      setRecShared(event.recordingShared || false);
    }
  }, [event?.recordingUrl, event?.recordingExpiry, event?.recordingShared]);

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={28} className="animate-spin" color="#7c3aed" />
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Montserrat, sans-serif' }}>Event not found</h2>
          <span role="button" tabIndex={0} onClick={() => navigate('/dashboard/services')} onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/services')}
            style={{ color: '#7c3aed', fontWeight: 600, fontSize: 14, cursor: 'pointer', userSelect: 'none', fontFamily: 'Montserrat, sans-serif', marginTop: 12, display: 'inline-block' }}>
            ← Back to Events
          </span>
        </div>
      </div>
    );
  }

  const activeRegs = registrations.filter(r => r.status === 'registered');
  const statusBadge = getStatusBadge(event.status);
  const spotsLeft = Math.max(0, (event.maxAttendees || 0) - (event.currentAttendees || 0));
  const totalRevenue = activeRegs.reduce((sum, r) => sum + (r.totalPaid || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <span role="button" tabIndex={0}
            onClick={() => navigate('/dashboard/services', { state: { tab: 'events' } })}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/services', { state: { tab: 'events' } })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', userSelect: 'none', marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Events
          </span>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>{event.title}</h1>
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
              </div>
              <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
                {formatEventSummary(event)} • {getFormatLabel(event.format)}
              </p>
            </div>
            <span role="button" tabIndex={0}
              onClick={() => navigate('/dashboard/services', { state: { tab: 'events', editEventId: event.id } })}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/services', { state: { tab: 'events', editEventId: event.id } })}
              style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#FFF', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
              Edit
            </span>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: 20, borderBottom: '2px solid #E5E7EB' }}>
            {['overview', 'attendees', 'communications'].map(tab => (
              <span key={tab} role="button" tabIndex={0}
                onClick={() => setActiveTab(tab)}
                onKeyDown={(e) => e.key === 'Enter' && setActiveTab(tab)}
                style={{
                  padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                  color: activeTab === tab ? '#7c3aed' : '#6B7280',
                  borderBottom: activeTab === tab ? '2px solid #7c3aed' : '2px solid transparent',
                  marginBottom: -2, transition: 'all 0.15s', textTransform: 'capitalize',
                }}>
                {tab === 'attendees' ? `Attendees (${activeRegs.length})` : tab}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px 80px' }}>
        {/* ──────────── Overview Tab ──────────── */}
        {activeTab === 'overview' && (
          <div>
            {/* Stats cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>{activeRegs.length}</p>
                <p style={{ fontSize: 13, color: '#6B7280', margin: 0, fontWeight: 500 }}>registered</p>
              </div>
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>
                  {event.pricingModel === 'free' ? 'Free' : `$${(totalRevenue / 100).toFixed(0)}`}
                </p>
                <p style={{ fontSize: 13, color: '#6B7280', margin: 0, fontWeight: 500 }}>collected</p>
              </div>
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>{spotsLeft}</p>
                <p style={{ fontSize: 13, color: '#6B7280', margin: 0, fontWeight: 500 }}>spots left</p>
              </div>
            </div>

            {/* Progress bar */}
            <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Registration progress</span>
                <span style={{ fontSize: 14, color: '#6B7280' }}>
                  {event.currentAttendees || 0} of {event.maxAttendees} ({event.maxAttendees > 0 ? Math.round(((event.currentAttendees || 0) / event.maxAttendees) * 100) : 0}%)
                </span>
              </div>
              <div style={{ height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 4, transition: 'width 0.3s',
                  width: `${event.maxAttendees > 0 ? Math.min(((event.currentAttendees || 0) / event.maxAttendees) * 100, 100) : 0}%`,
                  background: (event.currentAttendees || 0) >= event.maxAttendees ? '#D97706' : '#7c3aed',
                }} />
              </div>

              {/* Deposit stats */}
              {event.pricingModel === 'deposit' && activeRegs.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 13, color: '#6B7280' }}>
                  <span>Deposits collected: ${(totalRevenue / 100).toFixed(2)}</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>Total expected: ${(activeRegs.length * ((event.pricing?.totalPrice || 0) / 100)).toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Recording section — completed virtual events only */}
            {(event.status === 'completed' && (event.format === 'virtual' || event.format === 'hybrid')) && (
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <Video size={18} color="#7c3aed" />
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Recording</h3>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Recording URL</label>
                  <input
                    type="url"
                    value={recUrl}
                    onChange={(e) => setRecUrl(e.target.value)}
                    placeholder="https://zoom.us/rec/share/..."
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, fontFamily: 'Montserrat, sans-serif', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Availability</label>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {[{ value: 'seven_days', label: '7 days' }, { value: 'thirty_days', label: '30 days' }, { value: 'never', label: 'No expiration' }].map(opt => (
                      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: 'Montserrat, sans-serif', color: '#374151', cursor: 'pointer' }}>
                        <input type="radio" name="recExpiry" value={opt.value} checked={recExpiry === opt.value} onChange={() => setRecExpiry(opt.value)} style={{ accentColor: '#7c3aed' }} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {recShared ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0' }}>
                    <CheckCircle size={16} color="#16A34A" />
                    <span style={{ fontSize: 14, color: '#15803D', fontWeight: 500, fontFamily: 'Montserrat, sans-serif' }}>Recording shared with {activeRegs.length} attendee{activeRegs.length !== 1 ? 's' : ''}</span>
                  </div>
                ) : (
                  <button type="button" onClick={handleShareRecording} disabled={sharingRec || !recUrl.trim() || activeRegs.length === 0}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8,
                      border: 'none', background: '#7c3aed', color: '#FFF', fontWeight: 600, fontSize: 13,
                      fontFamily: 'Montserrat, sans-serif', cursor: sharingRec ? 'not-allowed' : 'pointer',
                      opacity: sharingRec || !recUrl.trim() || activeRegs.length === 0 ? 0.5 : 1,
                    }}>
                    {sharingRec ? 'Sharing...' : 'Share recording with attendees'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ──────────── Attendees Tab ──────────── */}
        {activeTab === 'attendees' && (
          <div>
            {/* Action bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Attendees ({activeRegs.length})</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={handleExportCSV} disabled={exporting || activeRegs.length === 0}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                    border: '1px solid #D1D5DB', background: '#FFF', color: '#374151', fontWeight: 600,
                    fontSize: 13, fontFamily: 'Montserrat, sans-serif', cursor: exporting ? 'not-allowed' : 'pointer',
                    opacity: exporting || activeRegs.length === 0 ? 0.5 : 1,
                  }}>
                  <Download size={14} />
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>

            {/* Empty state */}
            {activeRegs.length === 0 && (
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: '48px 24px', textAlign: 'center' }}>
                <Users size={32} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
                <p style={{ fontSize: 16, fontWeight: 600, color: '#6B7280', margin: '0 0 4px' }}>No attendees yet</p>
                <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Registrations will appear here as people sign up.</p>
              </div>
            )}

            {/* Attendee table */}
            {activeRegs.length > 0 && (
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1.2fr 1fr 0.5fr', gap: 12, padding: '12px 16px', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Name</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Email</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Registered</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Payment</span>
                  <span />
                </div>

                {/* Rows */}
                {activeRegs.map(reg => {
                  const payBadge = getPaymentBadge(reg.paymentStatus);
                  const regDate = reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
                  return (
                    <div key={reg.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1.2fr 1fr 0.5fr', gap: 12, padding: '12px 16px', borderBottom: '1px solid #F3F4F6', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {reg.attendeeName || '—'}
                      </span>
                      <span style={{ fontSize: 13, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {reg.attendeeEmail || '—'}
                      </span>
                      <span style={{ fontSize: 13, color: '#6B7280' }}>{regDate}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: payBadge.bg, color: payBadge.color, display: 'inline-block', width: 'fit-content' }}>
                        {payBadge.label}
                      </span>

                      {/* Actions menu */}
                      <div style={{ position: 'relative' }}>
                        <span role="button" tabIndex={0}
                          onClick={() => setMenuOpenId(menuOpenId === reg.id ? null : reg.id)}
                          onKeyDown={(e) => e.key === 'Enter' && setMenuOpenId(menuOpenId === reg.id ? null : reg.id)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', userSelect: 'none', color: '#9CA3AF' }}>
                          <MoreHorizontal size={16} />
                        </span>

                        {menuOpenId === reg.id && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setMenuOpenId(null)} />
                            <div style={{
                              position: 'absolute', right: 0, top: 32, zIndex: 20,
                              background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 160, overflow: 'hidden',
                            }}>
                              <span role="button" tabIndex={0}
                                onClick={() => { setMenuOpenId(null); handleRemoveAttendee(reg); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { setMenuOpenId(null); handleRemoveAttendee(reg); } }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                                  fontSize: 13, fontWeight: 500, color: '#DC2626', cursor: 'pointer', userSelect: 'none',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                <Trash2 size={14} /> Remove attendee
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ──────────── Communications Tab ──────────── */}
        {activeTab === 'communications' && (
          <div>
            {/* Compose */}
            <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Send a message to all attendees</h3>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Subject (optional)</label>
                <input
                  type="text"
                  value={msgSubject}
                  onChange={(e) => setMsgSubject(e.target.value)}
                  placeholder={`Update: ${event.title}`}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, fontFamily: 'Montserrat, sans-serif', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Message <span style={{ color: '#DC2626' }}>*</span></label>
                <textarea
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  placeholder="Write your message to attendees..."
                  rows={5}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, fontFamily: 'Montserrat, sans-serif', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" onClick={handleSendGroupMessage} disabled={sendingMsg || !msgBody.trim() || activeRegs.length === 0}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8,
                    border: 'none', background: '#7c3aed', color: '#FFF', fontWeight: 600, fontSize: 14,
                    fontFamily: 'Montserrat, sans-serif', cursor: sendingMsg ? 'not-allowed' : 'pointer',
                    opacity: sendingMsg || !msgBody.trim() || activeRegs.length === 0 ? 0.5 : 1,
                  }}>
                  <Send size={14} />
                  {sendingMsg ? 'Sending...' : `Send to ${activeRegs.length} attendee${activeRegs.length !== 1 ? 's' : ''}`}
                </button>
                {msgSuccess && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#16A34A', fontWeight: 500 }}>
                    <CheckCircle size={14} /> {msgSuccess}
                  </span>
                )}
              </div>
            </div>

            {/* Message history */}
            {(event.communications?.length > 0) && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>Message history</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[...(event.communications || [])].reverse().map((comm, i) => (
                    <div key={i} style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 18px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{comm.subject || 'No subject'}</span>
                        <span style={{ fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap', marginLeft: 12 }}>
                          {comm.sentAt ? new Date(comm.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        </span>
                      </div>
                      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 4px', lineHeight: 1.4 }}>
                        {(comm.message || '').substring(0, 150)}{(comm.message || '').length > 150 ? '...' : ''}
                      </p>
                      <span style={{ fontSize: 12, color: '#9CA3AF' }}>Sent to {comm.recipientCount} attendee{comm.recipientCount !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!event.communications || event.communications.length === 0) && (
              <div style={{ background: '#FFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: '40px 24px', textAlign: 'center' }}>
                <Mail size={28} color="#D1D5DB" style={{ margin: '0 auto 10px' }} />
                <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>No messages sent yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDashboard;
